<template>
  <div class="article-page">
    <header class="article-hero">
      <div class="article-hero__inner">
        <router-link to="/News" class="article-hero__back">
          <v-icon small color="white">mdi-arrow-left</v-icon> Νέα
        </router-link>
        <template v-if="article.id">
          <h1 class="article-hero__title">{{ article.title }}</h1>
          <div v-if="date" class="article-hero__date">
            <v-icon small color="rgba(255,255,255,0.8)">mdi-calendar-blank-outline</v-icon> {{ date }}
          </div>
        </template>
        <v-skeleton-loader v-else-if="loading" type="heading" dark class="mt-4" max-width="600" />
      </div>
    </header>

    <div class="article-content">
      <div v-if="notFound" class="article-missing">
        <p>Το άρθρο δεν βρέθηκε.</p>
        <v-btn color="#1b2f5a" dark depressed to="/News">Όλα τα νέα</v-btn>
      </div>

      <template v-else-if="article.id">
        <figure v-if="imageSrc && !imageFailed" class="article-cover">
          <img :src="imageSrc" :alt="article.title" @error="imageFailed = true">
        </figure>
        <div class="article-body" v-html="article.body"></div>
        <div class="article-actions">
          <v-btn outlined color="#1b2f5a" to="/News">
            <v-icon left>mdi-arrow-left</v-icon>Όλα τα νέα
          </v-btn>
        </div>
      </template>
    </div>

    <section v-if="related.length" class="article-related">
      <h2 class="article-related__title">Περισσότερα νέα</h2>
      <v-row>
        <v-col v-for="item in related" :key="item.id" cols="12" sm="6" md="4" class="align-self-stretch">
          <article-card :article="item" />
        </v-col>
      </v-row>
    </section>
  </div>
</template>

<script>
  import restApi from "@/rest-api";
  import ArticleCard from "@/components/ArticleCard.vue";
  import { formatAthensDate } from "@/utils/date";

  export default {
    components: { ArticleCard },
    data() {
        return {
            article: {},
            related: [],
            loading: false,
            notFound: false,
            imageFailed: false
        }
    },
    computed: {
      imageSrc() {
        return restApi.imageUrl(this.article.image)
      },
      date() {
        return formatAthensDate(this.article.createdAt)
      }
    },
    watch: {
      // opening another article from "Περισσότερα νέα" only changes the query
      '$route.query.articleId'(id) {
        if (id) {
          this.fetchArticle(id)
        }
      }
    },
    created() {
      this.fetchArticle(this.$route.query.articleId);
    },
    methods: {
      fetchArticle(id) {
        this.loading = true
        this.notFound = false
        this.imageFailed = false
        this.article = {}
        restApi
        .get(`/article/${id}`)
        .then(response => {
              if (!response || !response.article) {
                this.notFound = true
                return
              }
              this.article = response.article
              document.title = `${this.article.title} | PARON CARD`
              this.fetchRelated()
        })
        .catch(() => {
            this.notFound = true
        })
        .finally(() => {
            this.loading = false
        });
      },
      fetchRelated() {
        restApi
        .get(`/article/filtered?page=1&per_page=4`)
        .then(response => {
              this.related = (response.data || []).filter(item => item.id !== this.article.id).slice(0, 3)
        })
        .catch(() => {
            this.related = []
        });
      }
    }
  }
</script>

<style lang="scss" scoped>
.article-page {
    background: #fff;
}

.article-hero {
    background: linear-gradient(135deg, #1b2f5a 0%, #2c4a86 100%);
    color: #fff;
    padding: 40px 18px 56px;
}

.article-hero__inner {
    max-width: 820px;
    margin: 0 auto;
}

.article-hero__back {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: rgba(255, 255, 255, 0.85) !important;
    text-decoration: none;
    font-size: 15px;
    font-weight: 600;
}

.article-hero__back:hover {
    color: #fff !important;
}

.article-hero__title {
    margin: 16px 0 12px;
    font-size: clamp(26px, 4vw, 40px);
    font-weight: 700;
    line-height: 1.2;
}

.article-hero__date {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 15px;
    color: rgba(255, 255, 255, 0.8);
}

.article-content {
    max-width: 820px;
    margin: -32px auto 0;
    padding: 0 18px;
}

.article-cover {
    margin: 0 0 32px;
    border-radius: 16px;
    overflow: hidden;
    background: #eef1f7;
    box-shadow: 0 12px 32px rgba(27, 47, 90, 0.16);
    text-align: center;
}

.article-cover img {
    display: block;
    width: 100%;
    max-height: 520px;
    object-fit: contain;
    margin: 0 auto;
}

.article-body {
    color: #1b2f5a;
    font-size: 18px;
    line-height: 1.75;
    overflow-wrap: anywhere;
}

/* content comes from the CMS editor */
.article-body ::v-deep img,
.article-body ::v-deep iframe,
.article-body ::v-deep video {
    max-width: 100% !important;
    height: auto;
    border-radius: 10px;
}

.article-body ::v-deep p {
    margin-bottom: 1.1em;
}

.article-body ::v-deep a {
    color: #ad282d;
    text-decoration: underline;
}

.article-body ::v-deep h2,
.article-body ::v-deep h3 {
    margin: 1.4em 0 0.6em;
    line-height: 1.3;
}

.article-body ::v-deep table {
    width: 100% !important;
    border-collapse: collapse;
}

.article-actions {
    margin: 40px 0 8px;
    padding-top: 24px;
    border-top: 1px solid rgba(27, 47, 90, 0.12);
}

.article-missing {
    padding: 72px 0 24px;
    text-align: center;
    color: #1b2f5a;
}

.article-related {
    max-width: 1100px;
    margin: 48px auto 64px;
    padding: 0 18px;
}

.article-related__title {
    margin-bottom: 8px;
    color: #1b2f5a;
    font-size: 26px;
    font-weight: 700;
}
</style>
